import { useRef } from 'react'

/** True when `a` and `b` have the same keys and every value is `Object.is`-equal. */
export function shallowEqualObjects(a: object, b: object): boolean {
  if (a === b) return true

  const aKeys = Object.keys(a)
  const bKeys = Object.keys(b)

  if (aKeys.length !== bKeys.length) return false

  for (const key of aKeys) {
    if (
      !Object.prototype.hasOwnProperty.call(b, key) ||
      !Object.is((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key])
    ) {
      return false
    }
  }

  return true
}

/**
 * Returns `value`, or the previously returned value while `isEqual(previous, value)` holds.
 *
 * The general form of `useShallowStableValue`, for values whose equality isn't field-by-field.
 */
export function useStableValue<T>(value: T, isEqual: (previous: T, next: T) => boolean): T {
  const ref = useRef(value)

  if (ref.current !== value && !isEqual(ref.current, value)) {
    ref.current = value
  }

  return ref.current
}

function shallowEqualOrNull(a: object | null, b: object | null): boolean {
  return a === null || b === null ? a === b : shallowEqualObjects(a, b)
}

/**
 * Returns `value`, or the previously returned object while every field is still shallow-equal.
 *
 * Use it to build a context value (or a prop object for a memoized component) from values that are
 * recomputed on every render — including ones read from refs at render time, which `useMemo`
 * dependency lists can't see. The returned object changes identity only when its contents change,
 * so consumers re-render only then. Arrays compare element by element; `null` equals only `null`.
 */
export function useShallowStableValue<T extends object | null>(value: T): T {
  return useStableValue(value, shallowEqualOrNull)
}
